import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { SectionLabel } from "@/components/home/SectionLabel";
import { FADE_UP, SCALE_IN, FADE_UP_DELAYED } from "@/lib/animations";
import { SUBHEADLINE, IMAGES, TRUST_ICON } from "@/data/content";
import { ArrowRight } from "lucide-react";

const HERO_STATS = [
    { v: "50+", l: "Practices" },
    { v: "8", l: "Years" },
    { v: "35%", l: "AR reduction" },
];

export function HeroSection() {
    return (
        <section className="relative pt-[76px] overflow-hidden">
            <div className="glow-terracotta absolute -top-20 -right-32 w-[640px] h-[640px] pointer-events-none" />
            <div className="glow-sage absolute top-40 -left-40 w-[520px] h-[520px] pointer-events-none" />
            <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
                <div>
                    <motion.div {...FADE_UP}>
                        <span className="inline-flex items-center gap-2 border border-linec bg-cream/70 backdrop-blur px-3 py-1.5 rounded-full">
                            <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
                            <SectionLabel className="text-sage">Dental Revenue Cycle · U.S.-Based</SectionLabel>
                        </span>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink mt-6">
                            Your Dental Practice's{" "}
                            <span className="italic text-sage">Back Office,</span> Handled.
                        </h1>
                        <p className="text-base lg:text-lg text-inksoft leading-relaxed mt-7 max-w-xl">
                            {SUBHEADLINE}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 mt-9">
                            <Link
                                to="/contact"
                                data-testid="hero-cta-button"
                                className="inline-flex items-center justify-center gap-2 bg-terracotta text-cream px-8 py-4 rounded-full font-medium tracking-tight transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5 active:scale-95"
                            >
                                Get a Free Consultation
                                <ArrowRight size={18} />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-secondary-button"
                                className="inline-flex items-center justify-center gap-2 border border-linec text-ink px-8 py-4 rounded-full font-medium tracking-tight transition-colors duration-200 hover:bg-stone2 active:scale-95"
                            >
                                Explore Services
                            </Link>
                        </div>

                        <div className="flex items-center gap-8 mt-12">
                            {HERO_STATS.map((s) => (
                                <div key={s.l}>
                                    <div className="font-heading text-3xl text-sage">
                                        <AnimatedCounter value={s.v} />
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-inksoft mt-1">
                                        {s.l}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                <motion.div {...SCALE_IN} className="relative">
                    <div className="absolute -top-4 -left-4 w-full h-full border border-linec rounded-2xl hidden lg:block" />
                    <div className="img-frame relative rounded-2xl bg-stone2 border border-linec">
                        <img
                            src={IMAGES.hero}
                            alt="A back-office team collaborating with a dentist"
                            className="w-full h-[440px] lg:h-[520px] object-cover rounded-2xl"
                        />
                    </div>
                    <div className="absolute -bottom-5 left-5 lg:-left-5 bg-sage text-cream px-6 py-5 rounded-sm shadow-xl max-w-[220px]">
                        <TRUST_ICON size={22} className="text-terracotta mb-2" />
                        <p className="text-sm leading-snug">
                            HIPAA-compliant with a signed BAA on every engagement.
                        </p>
                    </div>
                    <motion.div
                        {...FADE_UP_DELAYED}
                        className="absolute -top-5 right-6 bg-cream border border-linec px-4 py-3 rounded-sm shadow-lg hidden sm:block"
                    >
                        <div className="font-heading text-2xl text-terracotta leading-none">
                            <AnimatedCounter value="Same-day" />
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-inksoft mt-1">
                            Verification
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
